import _ from "lodash";
import { ApplicationDataType } from "csv-package";

import { NarrativeTemplateType } from "../../helpers/narrativeGenerator";

const template: NarrativeTemplateType[] = [
  {
    template: "I seek to:",
    bold: true,
  },
  {
    conditions: {
      app_types: ({ app_types }) => _.isEqual(app_types, ["AE"]),
    },
    template: "Extend the length of the Intervention Order",
  },
  {
    conditions: {
      app_types: ({ app_types }) => _.isEqual(app_types, ["AV"]),
    },
    template: "Vary the conditions of the Intervention Order",
  },
  {
    conditions: {
      app_types: ({ app_types }) => _.isEqual(app_types, ["AE", "AV"]),
    },
    template: "Extend the length and vary the conditions of the Intervention Order"
  },
  {
    conditions: {
      app_types: ({ app_types }) => _.isEqual(app_types, ["AR"]),
    },
    template: "Revoke the Intervention Order",
  },
  {
    template: "I want to change:",
    conditions: {
      show: (values: any) => values.app_types && values.app_types.includes("AV") && values.vary_resp_other,
    },
    bold: true,
  },
  {
    template: `<%= vary_resp_other %>`,
    conditions: {
      show: (values: any) => values.app_types && values.app_types.includes("AV") && values.vary_resp_other,
    },
  },
  {
    conditions: {
      show: (values: any) => values.app_types && values.app_types.includes("AE"),
    },
    template: "I want to extend the order for:",
    bold: true,
  },
  {
    conditions: {
      show: (values: any) => values.app_types && values.app_types.includes("AE"),
    },
    template: "<%= app_type_extend_duration %>",
  },
  {
    template: "I want to remove the order because:",
    conditions: {
      show: (values: any) => values.app_types && values.app_types.includes("AR"),
    },
    bold: true,
  },
  {
    template: `<%= revoke_appl_why %>`,
    conditions: {
      show: (values: any) => values.app_types && values.app_types.includes("AR"),
    },
  },
  {
    template: "The respondent has committed family violence since the original order was made. More specific information about the incident(s):",
    conditions: {
      app_types: ["AE", "AV"],
      app_type_extend_further_violence_check: "yes",
    },
    bold: true,
  },
  {
    template: `<%= app_type_extend_why_breached %>`,
    conditions: {
      app_types: ["AE", "AV"],
      app_type_extend_further_violence_check: "yes",
    }
  },
  {
    template: "The Family Violence included:",
    conditions: {
      app_types: ["AE", "AV"],
      app_type_extend_further_violence_check: "yes",
    },
    bold: true,
  },
  {
    template: `<%= familyViolence %>`,
    conditions: {
      app_types: ["AE", "AV"],
      app_type_extend_further_violence_check: "yes",
    },
    variables: {
      familyViolence: (values) => {
        return getFamilyViolenceTexts(values) + (Array.isArray(values.app_type_extend_further_violence) && (values.app_type_extend_further_violence as string[]).includes("incident_fsw") ? `\n${values.incident_fsw_detail}` : "");
      }
    }
  },
  {
    conditions: {
      app_types: ["AE", "AV"],
      app_type_extend_further_violence_check: "no",
    },
    template: "I need to extend and or vary the order because:",
    bold: true,
  },
  {
    conditions: {
      show: (values: any) => values.app_types && (values.app_types.includes("AE") || values.app_types.includes("AV")),
      app_type_extend_further_violence_check: "no",
    },
    template: `<%= app_type_extend_why_violence_stopped %>`,
  },
  {
    conditions: {
      appl_pregnant_baby: "yes",
    },
    template: "Are you or the protected person(s) pregnant or have a child under 6 months?",
    bold: true,
  },
  {
    conditions: {
      appl_pregnant_baby: "yes",
    },
    template: `Yes`,
  },
];

export default template;

const getFamilyViolenceTexts = (values: ApplicationDataType) => {
  const bulletPointsMap: Record<string, any> = {
    incident_pys: {
      value: `Physically or sexually abused me or the protected person\n`,
      children: {
        physical_harm: {
          value: `Physical harm such as hitting, punching, pushing, pulling and/or kicking\n`,
        },
        choking_strangling: {
          value: `Choking, strangling or suffocating me or the protected person\n`,
        },
        using_weapons: {
          value: `Using weapons or other items to hurt me or the protected person\n`,
        },
        sexual_pressure: {
          value: `Pressuring me or the protected person sexually\n`,
        },
        child_harm: {
          value: `Physical or sexual harm of a child\n`,
        },
      },
    },
    incident_sty: {
      value: `Stalked or followed me or the protected person, either online or in person\n`,
    },
    incident_aep: {
      value: `Emotionally or psychologically abused me or the protected person\n`,
    },
    incident_cbp: {
      value: `Verbally abused me or the protected person\n`,
    },
    incident_cym: {
      value: `Economically abused me or the protected person\n`,
    },
    incident_thy: {
      value: `Threatened or coerced me or the protected person\n`,
      children: {
        threatened_kill_person: {
          value: `Threatened to kill me, a child, or a family member of mine or the protected person\n`,
        },
        threatened_harm_person: {
          value: `Threatened to harm me, a child, or a family member of mine or the protected person\n`,
        },
        threatened_kill_animal: {
          value: `Threatened to kill an animal or pet belonging to me or the protected person\n`,
        },
        threatened_self_harm: {
          value: `Threatened to harm or kill themselves\n`,
        },
      },
    },
    incident_cdw: {
      value: `Controlled or dominated me or the protected person in some other way\n`,
      children: {
        controlling: {
          value: `Controlling who I could see or speak to\n`,
        },
      },
    },
    incident_htc: {
      value: `Behaved in a way that caused a child to witness or be exposed to the above behaviour\n`,
    },
    incident_fsw: {
      value: "Other. Something else has happened, or I am worried something will happen, that warrants an extension to the order.\n",
    },
  };

  if (
    !values.app_type_extend_further_violence ||
    !_.isArray(values.app_type_extend_further_violence)
  ) {
    return "\n";
  }

  return Object.keys(bulletPointsMap).reduce(
    (text, key) => {
      if (!(values.app_type_extend_further_violence as string[]).includes(key)) {
        return text;
      }
      
      const item = bulletPointsMap[key];
      if (!item) return text;

      let result = text + item.value;
      const value = values[key];

      if (item.children && _.isArray(value)) {
        result += Object.keys(item.children).reduce((childText, childKey) => {
          if (!(value as string[]).includes(childKey)) {
            return childText;
          }
          const childItem = item.children![childKey];
          return childItem ? childText + "— " + childItem.value : childText;
        }, "");
      }

      return result;
    },
    "",
  );
}

